/**
 * De taal van een patroon: de regex-escape bij een controle (`regexVorm`) en bij een voorwaarde voor
 * zichtbaarheid (`voldoetAanPatroon`, `voldoetNietAanPatroon`), besluit van 1 november 2026, eisenboek 12.43.
 *
 * **Waarom een eigen taal en geen `RegExp`.** Een patroon komt van de inrichter en draait in de pod en in
 * de browser van de invuller. De reguliere expressies van JavaScript zoeken bij sommige patronen
 * exponentieel lang terug (`(a+)+$` op een lange rij a's met een b erachter), en een lopende `RegExp` is
 * niet te onderbreken. Daarom leest dit bestand een patroon naar een boom, en voert de engine die boom uit
 * met een automaat die elk teken van de invoer één keer bekijkt (`pattern.ts` in de engine). Dat is lineair
 * in de lengte van de invoer, wat het patroon ook is, en het hangt niet af van vlaggen of van de versie van
 * de JavaScript-engine.
 *
 * **De taal is een deel van die van `RegExp` met de vlag `u`, en rekent daar hetzelfde.** Wat erbinnen valt
 * geeft bij `new RegExp('^(?:' + patroon + ')$', 'u')` dezelfde uitkomst; de enginesuite bewijst dat op
 * willekeurige patronen en invoer, in Node en in Chromium. Wat erbuiten valt weigert dit bestand met de
 * reden, in de woorden van de inrichter: terugverwijzingen en vooruit- of terugkijken, die terugzoeken
 * vragen; woordgrenzen, benoemde groepen, Unicode-eigenschappen en `\c`, die niet bij een tekstvorm horen.
 *
 * **Het patroon geldt voor de hele waarde.** Een `^` vooraan en een `$` achteraan zijn daarom overbodig en
 * vallen weg; ergens anders hebben ze geen betekenis meer en worden ze geweigerd.
 *
 * Alleen Zod is een dependency van dit pakket, en dit bestand heeft er geen nodig: de schema's in
 * `rule.ts` roepen `patternProblem` aan.
 */

/** Het langste patroon, in UTF-16-eenheden zoals `string.length` telt (12.43). */
export const PATTERN_MAX_LENGTH = 200
/** De hoogste telling in `{n}`, `{n,}` of `{n,m}`. */
export const PATTERN_MAX_REPEAT = 100
/** Het grootste aantal toestanden van de automaat, na het uitschrijven van elke telling. */
export const PATTERN_MAX_STATES = 2000
/** De langste invoer die een patroon te zien krijgt, in tekens (code points); langer past nooit. */
export const PATTERN_MAX_INPUT = 10_000
/** Het grootste aantal voorbeelden per kant: wat past en wat niet past (D-19). */
export const PATTERN_MAX_EXAMPLES = 10

/** Een bereik van code points, met beide grenzen erbij. */
export type CodePointRange = readonly [number, number]

/** Een verzameling tekens: gesorteerde, niet-overlappende bereiken. */
export interface CharSet {
  readonly ranges: readonly CodePointRange[]
}

/** De boom van een patroon. Geen vangende groepen, geen ankers: die zijn er bij het lezen al uit. */
export type PatternNode =
  | { readonly kind: 'char'; readonly set: CharSet }
  | { readonly kind: 'sequence'; readonly parts: readonly PatternNode[] }
  | { readonly kind: 'choice'; readonly options: readonly PatternNode[] }
  | { readonly kind: 'repeat'; readonly part: PatternNode; readonly min: number; readonly max: number | undefined }

export type ParsedPattern =
  | { readonly ok: true; readonly node: PatternNode; readonly states: number }
  | { readonly ok: false; readonly reason: string }

const MAX_CODE_POINT = 0x10ffff

const DIGIT: CodePointRange[] = [[0x30, 0x39]]
const WORD: CodePointRange[] = [
  [0x30, 0x39],
  [0x41, 0x5a],
  [0x5f, 0x5f],
  [0x61, 0x7a],
]
/** Witruimte zoals `\s` in JavaScript: WhiteSpace en LineTerminator uit ECMA-262. */
const SPACE: CodePointRange[] = [
  [0x09, 0x0d],
  [0x20, 0x20],
  [0xa0, 0xa0],
  [0x1680, 0x1680],
  [0x2000, 0x200a],
  [0x2028, 0x2029],
  [0x202f, 0x202f],
  [0x205f, 0x205f],
  [0x3000, 0x3000],
  [0xfeff, 0xfeff],
]
/** Wat de punt niet neemt: de regeleinden van JavaScript. */
const LINE_TERMINATORS: CodePointRange[] = [
  [0x0a, 0x0a],
  [0x0d, 0x0d],
  [0x2028, 0x2029],
]

/** De tekens die met een backslash zichzelf worden, buiten en binnen een tekenklasse (IdentityEscape met `u`). */
const SYNTAX = new Set([...'^$\\.*+?()[]{}|/'].map((c) => c.codePointAt(0) as number))
const HYPHEN = 0x2d

function normalize(ranges: readonly CodePointRange[]): CodePointRange[] {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0])
  const out: [number, number][] = []
  for (const [lo, hi] of sorted) {
    const last = out.at(-1)
    if (last !== undefined && lo <= last[1] + 1) last[1] = Math.max(last[1], hi)
    else out.push([lo, hi])
  }
  return out
}

function complement(ranges: readonly CodePointRange[]): CodePointRange[] {
  const out: CodePointRange[] = []
  let next = 0
  for (const [lo, hi] of normalize(ranges)) {
    if (lo > next) out.push([next, lo - 1])
    next = hi + 1
  }
  if (next <= MAX_CODE_POINT) out.push([next, MAX_CODE_POINT])
  return out
}

function set(ranges: readonly CodePointRange[]): CharSet {
  return { ranges: normalize(ranges) }
}

/** Of dit teken in deze verzameling zit. Binair zoeken: een tekenklasse kan veel bereiken hebben. */
export function inCharSet(charSet: CharSet, codePoint: number): boolean {
  const { ranges } = charSet
  let low = 0
  let high = ranges.length - 1
  while (low <= high) {
    const middle = (low + high) >> 1
    const range = ranges[middle] as CodePointRange
    if (codePoint < range[0]) high = middle - 1
    else if (codePoint > range[1]) low = middle + 1
    else return true
  }
  return false
}

class Refusal extends Error {}

function refuse(reason: string): never {
  throw new Refusal(reason)
}

function shown(codePoint: number): string {
  return String.fromCodePoint(codePoint)
}

/** Het aantal toestanden van de automaat voor deze boom, zoals de engine hem bouwt. */
function statesOf(node: PatternNode): number {
  switch (node.kind) {
    case 'char':
      return 1
    case 'sequence':
      return node.parts.reduce((sum, part) => sum + statesOf(part), 0)
    case 'choice':
      return node.options.reduce((sum, option) => sum + statesOf(option), 0) + node.options.length - 1
    case 'repeat': {
      const each = statesOf(node.part)
      if (node.max === undefined) return node.min * each + each + 1
      return node.min * each + (node.max - node.min) * (each + 1)
    }
  }
}

/** Leest een patroon. Recursief afdalend over code points; elke weigering zegt waarom. */
class Reader {
  private position = 0
  private readonly points: readonly number[]

  constructor(expression: string) {
    this.points = [...expression].map((c) => c.codePointAt(0) as number)
  }

  read(): PatternNode {
    let end = this.points.length
    // Een ^ vooraan en een $ achteraan zijn overbodig: het patroon geldt al voor de hele waarde.
    if (this.points[0] === 0x5e) this.position = 1
    if (end > this.position && this.points[end - 1] === 0x24 && !this.escapedAt(end - 1)) end -= 1
    const node = this.choice(end, 0)
    if (this.position < end) {
      // `choice` stopt alleen voor een `)` die bij geen `(` hoort.
      refuse('er staat een haakje sluiten ) zonder haakje openen; schrijf \\) voor het teken zelf')
    }
    return node
  }

  /** Of het teken op deze plek met een backslash is ontsnapt: een oneven aantal backslashes ervoor. */
  private escapedAt(index: number): boolean {
    let count = 0
    for (let i = index - 1; i >= 0 && this.points[i] === 0x5c; i -= 1) count += 1
    return count % 2 === 1
  }

  private peek(end: number, offset = 0): number | undefined {
    const at = this.position + offset
    return at < end ? this.points[at] : undefined
  }

  private choice(end: number, depth: number): PatternNode {
    const options = [this.sequence(end, depth)]
    while (this.peek(end) === 0x7c) {
      this.position += 1
      options.push(this.sequence(end, depth))
    }
    return options.length === 1 ? (options[0] as PatternNode) : { kind: 'choice', options }
  }

  private sequence(end: number, depth: number): PatternNode {
    const parts: PatternNode[] = []
    for (;;) {
      const next = this.peek(end)
      if (next === undefined || next === 0x7c || next === 0x29) break
      parts.push(this.quantified(this.atom(end, depth), end))
    }
    return parts.length === 1 ? (parts[0] as PatternNode) : { kind: 'sequence', parts }
  }

  private quantified(node: PatternNode, end: number): PatternNode {
    const next = this.peek(end)
    let min: number
    let max: number | undefined
    if (next === 0x2a) {
      min = 0
      max = undefined
      this.position += 1
    } else if (next === 0x2b) {
      min = 1
      max = undefined
      this.position += 1
    } else if (next === 0x3f) {
      min = 0
      max = 1
      this.position += 1
    } else if (next === 0x7b) {
      const counted = this.count(end)
      if (counted === undefined) refuse('een { hoort bij een telling als {2}, {2,} of {2,5}; schrijf \\{ voor het teken zelf')
      ;[min, max] = counted
    } else {
      return node
    }
    // Een ? achter een herhaling maakt hem lui; bij een patroon dat de hele waarde moet dekken verandert
    // dat de uitkomst niet.
    if (this.peek(end) === 0x3f) this.position += 1
    const after = this.peek(end)
    if (after === 0x2a || after === 0x2b || after === 0x3f || (after === 0x7b && this.looksLikeCount(end))) {
      refuse('twee herhalingen achter elkaar: er is niets om te herhalen; zet het eerste deel tussen haakjes')
    }
    return { kind: 'repeat', part: node, min, max }
  }

  private looksLikeCount(end: number): boolean {
    const saved = this.position
    try {
      return this.count(end) !== undefined
    } catch {
      return true
    } finally {
      this.position = saved
    }
  }

  /** Een telling `{n}`, `{n,}` of `{n,m}`, of niets als hier geen telling staat. */
  private count(end: number): [number, number | undefined] | undefined {
    const start = this.position
    this.position += 1
    const first = this.digits(end)
    if (first === undefined) {
      this.position = start
      return undefined
    }
    let second: number | undefined = first
    if (this.peek(end) === 0x2c) {
      this.position += 1
      second = this.digits(end)
    }
    if (this.peek(end) !== 0x7d) {
      this.position = start
      return undefined
    }
    this.position += 1
    if (first > PATTERN_MAX_REPEAT || (second !== undefined && second > PATTERN_MAX_REPEAT)) {
      refuse(`een telling is ten hoogste ${String(PATTERN_MAX_REPEAT)}`)
    }
    if (second !== undefined && second < first) refuse(`in de telling {${String(first)},${String(second)}} staat het grootste getal voorop`)
    return [first, second]
  }

  private digits(end: number): number | undefined {
    let text = ''
    for (let next = this.peek(end); next !== undefined && next >= 0x30 && next <= 0x39; next = this.peek(end)) {
      text += shown(next)
      this.position += 1
    }
    // Een heel lang getal wordt niet eerst een `Infinity`: alles boven de grens is te groot.
    return text === '' ? undefined : text.length > 6 ? PATTERN_MAX_REPEAT + 1 : Number(text)
  }

  private atom(end: number, depth: number): PatternNode {
    const next = this.points[this.position] as number
    switch (next) {
      case 0x28:
        return this.group(end, depth)
      case 0x5b:
        return { kind: 'char', set: this.charClass(end) }
      case 0x2e:
        this.position += 1
        return { kind: 'char', set: set(complement(LINE_TERMINATORS)) }
      case 0x5c:
        return { kind: 'char', set: this.escape(end, false).set }
      case 0x2a:
      case 0x2b:
      case 0x3f:
        return refuse(`${shown(next)} staat waar niets om te herhalen is; schrijf \\${shown(next)} voor het teken zelf`)
      case 0x7b:
        if (this.looksLikeCount(end)) refuse('een telling staat waar niets om te herhalen is')
        return refuse('een { hoort bij een telling als {2}, {2,} of {2,5}; schrijf \\{ voor het teken zelf')
      case 0x7d:
        return refuse('een } zonder telling ervoor; schrijf \\} voor het teken zelf')
      case 0x5d:
        return refuse('een ] zonder [ ervoor; schrijf \\] voor het teken zelf')
      case 0x5e:
        return refuse('^ mag alleen vooraan staan, en is daar overbodig: het patroon geldt al voor de hele waarde; schrijf \\^ voor het teken zelf')
      case 0x24:
        return refuse('$ mag alleen achteraan staan, en is daar overbodig: het patroon geldt al voor de hele waarde; schrijf \\$ voor het teken zelf')
      default:
        this.position += 1
        return { kind: 'char', set: set([[next, next]]) }
    }
  }

  private group(end: number, depth: number): PatternNode {
    this.position += 1
    if (this.peek(end) === 0x3f) {
      const kind = this.peek(end, 1)
      const after = this.peek(end, 2)
      if (kind === 0x3a) {
        this.position += 2
      } else if (kind === 0x3d || kind === 0x21) {
        refuse('vooruitkijken met (?= of (?! kent een patroon niet: het vraagt terugzoeken')
      } else if (kind === 0x3c && (after === 0x3d || after === 0x21)) {
        refuse('terugkijken met (?<= of (?<! kent een patroon niet: het vraagt terugzoeken')
      } else if (kind === 0x3c) {
        refuse('een benoemde groep (?<naam>...) kent een patroon niet; gebruik (...) of (?:...)')
      } else {
        refuse('na (? hoort een : te staan, zoals in (?:...)')
      }
    }
    const inner = this.choice(end, depth + 1)
    if (this.peek(end) !== 0x29) refuse('er mist een haakje sluiten )')
    this.position += 1
    return inner
  }

  /**
   * Een tekenklasse `[...]` of `[^...]`. Zoals met de vlag `u`: een `-` vooraan of achteraan is het teken
   * zelf, een bereik met `\d` of een andere klasse als grens is een fout, en `[` binnen de klasse is het
   * teken zelf.
   */
  private charClass(end: number): CharSet {
    this.position += 1
    let negated = false
    if (this.peek(end) === 0x5e) {
      negated = true
      this.position += 1
    }
    const ranges: CodePointRange[] = []
    for (;;) {
      const next = this.peek(end)
      if (next === undefined) refuse('de tekenklasse [ wordt niet afgesloten met ]')
      if (next === 0x5d) {
        this.position += 1
        break
      }
      const low = this.classAtom(end)
      if (this.peek(end) === HYPHEN && this.peek(end, 1) !== undefined && this.peek(end, 1) !== 0x5d) {
        this.position += 1
        const high = this.classAtom(end)
        if (low.single === undefined || high.single === undefined) refuse('een bereik in een tekenklasse heeft twee tekens als grens, geen \\d, \\w of \\s')
        if (high.single < low.single) refuse(`het bereik ${shown(low.single)}-${shown(high.single)} staat in de verkeerde volgorde`)
        ranges.push([low.single, high.single])
      } else {
        ranges.push(...low.set.ranges)
      }
    }
    return set(negated ? complement(ranges) : ranges)
  }

  private classAtom(end: number): { readonly set: CharSet; readonly single: number | undefined } {
    const next = this.points[this.position] as number
    if (next === 0x5c) return this.escape(end, true)
    this.position += 1
    return { set: set([[next, next]]), single: next }
  }

  /** Een ontsnapping met `\`, buiten of binnen een tekenklasse. */
  private escape(end: number, inClass: boolean): { readonly set: CharSet; readonly single: number | undefined } {
    this.position += 1
    const next = this.peek(end)
    if (next === undefined) refuse('het patroon eindigt op een \\; schrijf \\\\ voor het teken zelf')
    this.position += 1
    const one = (codePoint: number): { set: CharSet; single: number } => ({ set: set([[codePoint, codePoint]]), single: codePoint })
    const many = (ranges: readonly CodePointRange[]): { set: CharSet; single: undefined } => ({ set: set(ranges), single: undefined })
    switch (shown(next)) {
      case 'd':
        return many(DIGIT)
      case 'D':
        return many(complement(DIGIT))
      case 'w':
        return many(WORD)
      case 'W':
        return many(complement(WORD))
      case 's':
        return many(SPACE)
      case 'S':
        return many(complement(SPACE))
      case 't':
        return one(0x09)
      case 'n':
        return one(0x0a)
      case 'v':
        return one(0x0b)
      case 'f':
        return one(0x0c)
      case 'r':
        return one(0x0d)
      case '0': {
        const after = this.peek(end)
        if (after !== undefined && after >= 0x30 && after <= 0x39) refuse('na \\0 mag geen cijfer staan')
        return one(0)
      }
      case 'x': {
        const value = this.hex(end, 2)
        if (value === undefined) refuse('na \\x horen twee hexadecimale cijfers, zoals \\x41')
        return one(value)
      }
      case 'u':
        return one(this.unicode(end))
      case 'b':
      case 'B':
        return refuse('een woordgrens \\b of \\B kent een patroon niet')
      case 'k':
        return refuse('een terugverwijzing \\k<naam> kent een patroon niet: die vraagt terugzoeken')
      case 'p':
      case 'P':
        return refuse('een Unicode-eigenschap \\p{...} kent een patroon niet; gebruik een tekenklasse')
      case 'c':
        return refuse('een stuurteken met \\c kent een patroon niet; gebruik \\x of \\u')
      default:
        break
    }
    if (next >= 0x31 && next <= 0x39) refuse('een terugverwijzing als \\1 kent een patroon niet: die vraagt terugzoeken')
    if (SYNTAX.has(next) || (inClass && next === HYPHEN)) return one(next)
    return refuse(`\\${shown(next)} is geen ontsnapping die een patroon kent; ontsnap alleen tekens als . * + ? ( ) [ ] { } | ^ $ / en \\`)
  }

  private hex(end: number, length: number): number | undefined {
    let text = ''
    for (let i = 0; i < length; i += 1) {
      const next = this.peek(end)
      if (next === undefined || !/[0-9a-fA-F]/.test(shown(next))) return undefined
      text += shown(next)
      this.position += 1
    }
    return parseInt(text, 16)
  }

  /** `\uHHHH`, `\u{H...}`, en een paar surrogaten als `😀` als één teken, zoals met de vlag `u`. */
  private unicode(end: number): number {
    if (this.peek(end) === 0x7b) {
      this.position += 1
      let text = ''
      while (this.peek(end) !== undefined && this.peek(end) !== 0x7d) {
        text += shown(this.points[this.position] as number)
        this.position += 1
      }
      if (this.peek(end) !== 0x7d || !/^[0-9a-fA-F]+$/.test(text) || parseInt(text, 16) > MAX_CODE_POINT) {
        refuse('na \\u{ hoort een hexadecimaal getal tot 10FFFF en een }, zoals \\u{1F600}')
      }
      this.position += 1
      return parseInt(text, 16)
    }
    const value = this.hex(end, 4)
    if (value === undefined) refuse('na \\u horen vier hexadecimale cijfers, zoals \\u00E9, of \\u{...}')
    if (value >= 0xd800 && value <= 0xdbff && this.peek(end) === 0x5c && this.peek(end, 1) === 0x75) {
      const saved = this.position
      this.position += 2
      const low = this.hex(end, 4)
      if (low !== undefined && low >= 0xdc00 && low <= 0xdfff) return (value - 0xd800) * 0x400 + (low - 0xdc00) + 0x10000
      this.position = saved
    }
    return value
  }
}

/** Leest een patroon naar zijn boom, of zegt waarom het geen patroon is. */
export function parsePattern(expression: string): ParsedPattern {
  if (expression === '') return { ok: false, reason: 'het patroon is leeg' }
  if (expression.length > PATTERN_MAX_LENGTH) return { ok: false, reason: `een patroon is ten hoogste ${String(PATTERN_MAX_LENGTH)} tekens` }
  let node: PatternNode
  try {
    node = new Reader(expression).read()
  } catch (error) {
    if (error instanceof Refusal) return { ok: false, reason: error.message }
    throw error
  }
  const states = statesOf(node) + 1
  if (states > PATTERN_MAX_STATES) {
    return { ok: false, reason: `het patroon wordt te groot: ${String(states)} toestanden, en ten hoogste ${String(PATTERN_MAX_STATES)} mag; maak de tellingen kleiner` }
  }
  return { ok: true, node, states }
}

/** Wat er aan een patroon mankeert, als bijzin (kleine letter, geen punt), of niets. */
export function patternProblem(expression: string): string | undefined {
  const parsed = parsePattern(expression)
  return parsed.ok ? undefined : parsed.reason
}
